import { useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { METER_KINDS, newId, type MeterKind } from '@gk/contracts'
import { useClub, useEngineState } from '../club/ClubProvider.tsx'
import { type ObservationRow, type SlotView, useMeterStatus, useSlotViews } from '../data/meters.ts'
import type { AssetRow } from '../data/fleet.ts'
import { ASSET_SECTIONS } from '../data/workshopUi.ts'
import { fmtNum, parseDecimal } from '../lib/format.ts'
import { formatLocal } from '../lib/time.ts'
import type { Pending } from '../sync/merge.ts'
import { Banner, DisabledHint, Field, PendingBanner, Pill, Section } from '../ui/components.tsx'
import { useAction, useWorkshopAction, useWriteAccess } from '../ui/hooks.ts'
import { FoldSummary, useCloseOnPending } from './workshop/shared.tsx'


/** The meters of one asset (GK-006, GK-007). Reading is a field fact: it works offline and shows at once. Everything else (define,
 *  correct, replace, estimate, review) is the workshop's and waits for the server's answer. The server alone decides what counts. */
export function MeterPanel({ asset }: { asset: Pending<AssetRow> }) {
  const { t } = useTranslation()
  const views = useSlotViews(asset.id)
  const signature = (views ?? []).map((v) => `${v.slot.id}:${v.observations.length}:${v.latest?.id ?? ''}:${v.pendingReview}:${v.instances.length}`).join('|')
  const status = useMeterStatus(asset.id, signature)
  const manage = useWriteAccess('meter.manage')
  const define = useWriteAccess('asset.manage')
  const online = useEngineState().online !== false
  if (!views) return null
  const free = METER_KINDS.filter((k) => !views.some((v) => v.slot.kind === k))
  return (
    <div id={ASSET_SECTIONS.meters} className="scroll-mt-20">
      <Section title={t('meters.title')} testId="meter-panel">
        {views.length === 0 && <p className="opacity-70">{t('meters.none')}</p>}
        {views.map((v) => <SlotCard key={v.slot.id} view={v} status={status.data?.find((s) => s.slot_id === v.slot.id)} canManage={manage.allowed && online} reload={status.reload} />)}
        {views.length > 0 && manage.permitted && !online && <p className="meta">{t('meters.onlineNote')}</p>}
        {define.allowed && online && free.length > 0 && <DefineSlotForm asset={asset} free={free} reload={status.reload} />}
      </Section>
    </div>
  )
}

function Value({ n, unit, className = '' }: { n: number; unit: string; className?: string }) {
  const { t, i18n } = useTranslation()
  return <span className={className}>{fmtNum(n, i18n.language)} {t(`meters.units.${unit}`)}</span>
}

function SlotCard({ view, status, canManage, reload }: { view: SlotView; status?: { lifetime_value: number; opening_value: number | null; current_value: number | null }; canManage: boolean; reload: () => void }) {
  const { t } = useTranslation()
  const { site } = useClub()
  const { slot, latest } = view
  return (
    <div className="card flex flex-col gap-3" data-testid={`slot-${slot.kind}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold opacity-70">{t(`meters.kinds.${slot.kind}`)}</p>
          {latest ? (
            <>
              <p className="text-3xl font-bold" data-testid="meter-latest"><Value n={latest.value} unit={slot.unit_code} /></p>
              <p className="meta">{t('meters.readAt', { when: formatLocal(latest.observed_at, site.timezone, 'dd/MM/yyyy HH:mm') })}</p>
            </>
          ) : view.open ? (
            <p className="text-lg opacity-80" data-testid="meter-opening">{t('meters.opening')} : <Value n={view.open.baseline_value} unit={slot.unit_code} className="font-bold" /></p>
          ) : <p className="text-lg opacity-70">{t('meters.noReading')}</p>}
        </div>
        <div className="flex flex-col items-end gap-1">{latest && <Pills o={latest} />}</div>
      </div>
      {status && view.instances.length > 0 && (
        <p className="text-sm opacity-80" data-testid="meter-lifetime">
          {t('meters.lifetime')} : <strong><Value n={status.lifetime_value} unit={slot.unit_code} /></strong>
          {view.instances.length > 1 && <> · {t('meters.counters', { count: view.instances.length })}</>}
        </p>
      )}
      <RecordForm view={view} />
      <History view={view} canManage={canManage} reload={reload} />
      {canManage && view.open && <><EstimateForm view={view} reload={reload} /><ReplaceForm view={view} reload={reload} /></>}
    </div>
  )
}

/** The state words of a reading. A reading to verify says WHY when the server flagged its quality (M23 item 22: "Inférieur à un relevé voisin" instead of a bare "À vérifier"). */
function Pills({ o }: { o: Pending<ObservationRow> }) {
  const { t } = useTranslation()
  return (
    <>
      {o._pending && <Pill tone="amber">{t('meters.pill.pending')}</Pill>}
      {o.source === 'estimated' && <Pill tone="gray">{t('meters.pill.estimate')}</Pill>}
      {o.source === 'import' && <Pill tone="gray">{t('meters.pill.imported')}</Pill>}
      {o.is_final && <Pill tone="gray">{t('meters.pill.final')}</Pill>}
      {o.quality !== 'ok' && <Pill tone={o.review_state === 'pending' ? 'red' : 'gray'}>{t(`meters.quality.${o.quality}`)}</Pill>}
      {o.review_state === 'pending' && o.quality === 'ok' && <Pill tone="red">{t('meters.pill.toReview')}</Pill>}
      {o.review_state === 'accepted' && <Pill tone="green">{t('meters.pill.accepted')}</Pill>}
      {o.review_state === 'rejected' && <Pill tone="gray">{t('meters.pill.rejected')}</Pill>}
    </>
  )
}

// ---------------------------------------------------------------------------------------------------- reading (a field fact)
export function RecordForm({ view }: { view: SlotView }) {
  const { t, i18n } = useTranslation()
  const { send } = useClub()
  const engine = useEngineState()
  const access = useWriteAccess('meter.record')
  const { busy, error, run } = useAction()
  const obsId = useRef(newId())
  const [raw, setRaw] = useState('')
  const [warn, setWarn] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  if (!access.permitted || !view.open) return null
  const open = view.open

  function submit(e: FormEvent, force = false) {
    e.preventDefault(); setDone(null)
    void run(async () => {
      const n = parseDecimal(raw)
      if (n === null) throw new Error(t('meters.invalid'))
      if (!force && view.latest && n < view.latest.value) { setWarn(t('meters.lower', { last: fmtNum(view.latest.value, i18n.language) })); return }
      setWarn(null)
      await send('meter.record@1', { observation_id: obsId.current, slot_id: view.slot.id, value: n, assumed_instance_id: open.id })
      obsId.current = newId(); setRaw('')
      setDone(engine.online === false || !navigator.onLine ? t('meters.recordedOffline') : t('meters.recorded'))
    })
  }
  return (
    <form className="flex flex-col gap-3 border-t pt-3" onSubmit={(e) => submit(e)} data-testid={`record-${view.slot.kind}`}>
      <Field label={`${t('meters.record')} (${t(`meters.units.${view.slot.unit_code}`)})`} htmlFor={`rec-${view.slot.id}`}>
        <input id={`rec-${view.slot.id}`} className="input" inputMode="decimal" autoComplete="off" value={raw} onChange={(e) => { setRaw(e.target.value); setWarn(null) }} data-testid="meter-value" />
      </Field>
      {warn && <Banner tone="warn">{warn}</Banner>}
      {error && <Banner tone="error">{error}</Banner>}
      {done && <Banner tone="ok">{done}</Banner>}
      <div className="grid gap-3">
        {warn
          ? <button type="button" className="btn btn-primary" onClick={(e) => submit(e as unknown as FormEvent, true)} data-testid="meter-confirm">{t('meters.confirmAnyway')}</button>
          : <>
              <DisabledHint show={!raw.trim() && !access.readOnlyMode} testId="meter-save-hint">{t('meters.recordHint')}</DisabledHint>
              <button className="btn btn-primary" disabled={busy || access.readOnlyMode || !raw.trim()} data-testid="meter-save">{t('meters.recordGo')}</button>
            </>}
      </div>
    </form>
  )
}

// ---------------------------------------------------------------------------------------------------- history and the mechanic's actions
function History({ view, canManage, reload }: { view: SlotView; canManage: boolean; reload: () => void }) {
  const { t } = useTranslation()
  const { site } = useClub()
  const [editing, setEditing] = useState<string | null>(null)
  const [kept, setKept] = useState(false)
  const act = useWorkshopAction()
  const unit = view.slot.unit_code
  if (view.observations.length === 0) return null
  return (
    <details className="group border-t pt-3" data-testid="meter-history">
      <FoldSummary>{t('meters.history')} ({view.observations.length})</FoldSummary>
      {act.error && <Banner tone="error">{act.error}</Banner>}
      {act.ok && <Banner tone="ok">{act.ok}</Banner>}
      {act.pending && <PendingBanner />}
      {kept && <PendingBanner />}
      <ul className="mt-2 flex flex-col gap-2">
        {view.observations.map((o) => {
          const superseded = !!o.superseded_by_id
          const lateEntry = Math.abs(new Date(o.entered_at).getTime() - new Date(o.observed_at).getTime()) > 5 * 60_000
          return (
            <li key={o.id} className="rounded-xl border-2 border-slate-300 p-3 dark:border-slate-600" data-testid="meter-obs">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className={`text-lg font-bold ${superseded ? 'line-through opacity-60' : ''}`}><Value n={o.value} unit={unit} /></span>
                <span className="flex flex-wrap gap-1">
                  {superseded && <Pill tone="gray">{t('meters.pill.corrected')}</Pill>}
                  <Pills o={o} />
                </span>
              </div>
              <p className="meta">
                {formatLocal(o.observed_at, site.timezone, 'dd/MM/yyyy HH:mm')}
                {lateEntry && <> · {t('meters.entered', { when: formatLocal(o.entered_at, site.timezone, 'dd/MM/yyyy HH:mm') })}</>}
                {o.estimate_method && <> · {o.estimate_method}</>}
              </p>
              {o.correction_reason && <p className="meta">{t('meters.correction', { reason: o.correction_reason })}</p>}
              {canManage && !superseded && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {o.review_state === 'pending' && (['accept', 'reject'] as const).map((d) => (
                    <button key={d} className={`btn !min-h-11 !px-4 ${d === 'accept' ? 'btn-primary' : ''}`} disabled={act.busy} data-testid={`obs-${d}`}
                      onClick={() => void act.run('meter.review@1', { observation_id: o.id, decision: d }, 'meters.reviewed').then(reload)}>{t(`meters.${d}`)}</button>
                  ))}
                  <button className="btn !min-h-11 !px-4" onClick={() => { setKept(false); setEditing(editing === o.id ? null : o.id) }} data-testid="obs-correct">{t('meters.correct')}</button>
                </div>
              )}
              {editing === o.id && <CorrectForm original={o} onDone={() => { setEditing(null); reload() }} onPending={() => { setKept(true); setEditing(null) }} />}
            </li>
          )
        })}
      </ul>
    </details>
  )
}

function CorrectForm({ original, onDone, onPending }: { original: Pending<ObservationRow>; onDone: () => void; onPending: () => void }) {
  const { t } = useTranslation()
  const act = useWorkshopAction()
  const id = useRef(newId())
  const [raw, setRaw] = useState('')
  const [reason, setReason] = useState('')
  useCloseOnPending(act.pending, onPending)
  return (
    <form className="mt-3 flex flex-col gap-3" onSubmit={(e) => {
      e.preventDefault()
      const n = parseDecimal(raw)
      if (n === null) return
      void act.run('meter.correct@1', { observation_id: id.current, corrects_observation_id: original.id, value: n, reason: reason.trim() }, 'meters.corrected').then((ok) => { if (ok) onDone() })
    }}>
      <Field label={t('meters.correctValue')} htmlFor={`cv-${original.id}`}><input id={`cv-${original.id}`} className="input" inputMode="decimal" value={raw} onChange={(e) => setRaw(e.target.value)} data-testid="correct-value" /></Field>
      <Field label={t('meters.reason')} htmlFor={`cr-${original.id}`}><input id={`cr-${original.id}`} className="input" maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="correct-reason" /></Field>
      {act.error && <Banner tone="error">{act.error}</Banner>}
      <DisabledHint show={!raw.trim() || !reason.trim()}>{t('meters.correctHint')}</DisabledHint>
      <button className="btn btn-primary" disabled={act.busy || !raw.trim() || !reason.trim()} data-testid="correct-go">{t('meters.correctGo')}</button>
    </form>
  )
}

// ---------------------------------------------------------------------------------------------------- estimate, replace, define
function EstimateForm({ view, reload }: { view: SlotView; reload: () => void }) {
  const { t } = useTranslation()
  const act = useWorkshopAction()
  const id = useRef(newId())
  const [raw, setRaw] = useState('')
  const [method, setMethod] = useState('')
  useCloseOnPending(act.pending, () => { setRaw(''); setMethod('') })
  return (
    <details className="group border-t pt-3">
      <FoldSummary>{t('meters.estimate')}</FoldSummary>
      <form className="mt-3 flex flex-col gap-3" onSubmit={(e) => {
        e.preventDefault()
        const n = parseDecimal(raw)
        if (n === null) return
        void act.run('meter.record_estimate@1', { observation_id: id.current, slot_id: view.slot.id, value: n, estimate_method: method.trim() }, 'meters.estimated').then((ok) => { if (ok) { id.current = newId(); setRaw(''); setMethod(''); reload() } })
      }}>
        <p className="meta">{t('meters.estimateHelp')}</p>
        <Field label={t('meters.value')} htmlFor={`ev-${view.slot.id}`}><input id={`ev-${view.slot.id}`} className="input" inputMode="decimal" value={raw} onChange={(e) => setRaw(e.target.value)} data-testid="estimate-value" /></Field>
        <Field label={t('meters.method')} htmlFor={`em-${view.slot.id}`}><input id={`em-${view.slot.id}`} className="input" maxLength={80} value={method} onChange={(e) => setMethod(e.target.value)} data-testid="estimate-method" /></Field>
        {act.error && <Banner tone="error">{act.error}</Banner>}
        {act.ok && <Banner tone="ok">{act.ok}</Banner>}
        {act.pending && <PendingBanner />}
        <button className="btn" disabled={act.busy || !raw.trim() || !method.trim()} data-testid="estimate-go">{t('meters.estimateGo')}</button>
      </form>
    </details>
  )
}

function ReplaceForm({ view, reload }: { view: SlotView; reload: () => void }) {
  const { t } = useTranslation()
  const act = useWorkshopAction()
  const ids = useRef({ instance: newId(), final: newId() })
  const [fin, setFin] = useState('')
  const [base, setBase] = useState('0')
  const [reason, setReason] = useState('')
  useCloseOnPending(act.pending, () => { setFin(''); setReason('') })
  return (
    <details className="group border-t pt-3">
      <FoldSummary>{t('meters.replace')}</FoldSummary>
      <form className="mt-3 flex flex-col gap-3" onSubmit={(e) => {
        e.preventDefault()
        const f = parseDecimal(fin), b = parseDecimal(base)
        if (f === null || b === null) return
        void act.run('meter.replace@1', { instance_id: ids.current.instance, slot_id: view.slot.id, final_observation_id: ids.current.final, final_value: f, new_baseline_value: b, ...(reason.trim() ? { reason: reason.trim() } : {}) }, 'meters.replaced')
          .then((ok) => { if (ok) { ids.current = { instance: newId(), final: newId() }; setFin(''); setBase('0'); setReason(''); reload() } })
      }}>
        <p className="meta">{t('meters.replaceHelp')}</p>
        <Field label={t('meters.finalValue')} htmlFor={`rf-${view.slot.id}`}><input id={`rf-${view.slot.id}`} className="input" inputMode="decimal" value={fin} onChange={(e) => setFin(e.target.value)} data-testid="replace-final" /></Field>
        <Field label={t('meters.newBaseline')} htmlFor={`rb-${view.slot.id}`}><input id={`rb-${view.slot.id}`} className="input" inputMode="decimal" value={base} onChange={(e) => setBase(e.target.value)} data-testid="replace-baseline" /></Field>
        <Field label={`${t('meters.reason')} (${t('common.optional').toLowerCase()})`} htmlFor={`rr-${view.slot.id}`}><input id={`rr-${view.slot.id}`} className="input" maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
        {act.error && <Banner tone="error">{act.error}</Banner>}
        {act.ok && <Banner tone="ok">{act.ok}</Banner>}
        {act.pending && <PendingBanner />}
        <button className="btn" disabled={act.busy || !fin.trim() || !base.trim()} data-testid="replace-go">{t('meters.replaceGo')}</button>
      </form>
    </details>
  )
}

function DefineSlotForm({ asset, free, reload }: { asset: Pending<AssetRow>; free: readonly MeterKind[]; reload: () => void }) {
  const { t } = useTranslation()
  const act = useWorkshopAction()
  const ids = useRef({ slot: newId(), instance: newId() })
  const [kind, setKind] = useState<MeterKind>(free[0]!)
  const [raw, setRaw] = useState('')
  useCloseOnPending(act.pending, () => setRaw(''))
  return (
    <form className="card flex flex-col gap-3" data-testid="define-slot" onSubmit={(e) => {
      e.preventDefault()
      const n = parseDecimal(raw)
      if (n === null) return
      void act.run('meter.define_slot@1', { slot_id: ids.current.slot, instance_id: ids.current.instance, asset_id: asset.id, kind, baseline_value: n }, 'meters.defined')
        .then((ok) => { if (ok) { ids.current = { slot: newId(), instance: newId() }; setRaw(''); reload() } })
    }}>
      <h3 className="text-lg font-bold">{t('meters.define')}</h3>
      <Field label={t('meters.kind')} htmlFor="slot-kind">
        <select id="slot-kind" className="input" value={free.includes(kind) ? kind : free[0]} onChange={(e) => setKind(e.target.value as MeterKind)}>
          {free.map((k) => <option key={k} value={k}>{t(`meters.kinds.${k}`)}</option>)}
        </select>
      </Field>
      <Field label={t('meters.baseline')} htmlFor="slot-baseline"><input id="slot-baseline" className="input" inputMode="decimal" value={raw} onChange={(e) => setRaw(e.target.value)} data-testid="slot-baseline" /></Field>
      {act.error && <Banner tone="error">{act.error}</Banner>}
      {act.ok && <Banner tone="ok">{act.ok}</Banner>}
      {act.pending && <PendingBanner />}
      <DisabledHint show={!raw.trim()} testId="slot-go-hint">{t('meters.defineHint')}</DisabledHint>
      <button className="btn btn-primary" disabled={act.busy || !raw.trim()} data-testid="slot-go">{t('meters.defineGo')}</button>
    </form>
  )
}

/** The readings a person takes while working (a start or an end hour count): one form per open counter of the machine. Same field fact as on the machine's page.
 *  The heading comes after the null check (M23 item 53): a machine with no counter shows no empty "Relevé du compteur" title. */
export function QuickReadings({ assetId, title }: { assetId: string; title?: string }) {
  const views = useSlotViews(assetId)
  const open = (views ?? []).filter((v) => v.open)
  if (open.length === 0) return null
  return (
    <div className="flex flex-col gap-2" data-testid="quick-readings">
      {title && <h2 className="text-lg font-bold">{title}</h2>}
      {open.map((v) => <RecordForm key={v.slot.id} view={v} />)}
    </div>
  )
}
